import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Collections } from '@smart-tv/components/collections';
import useStripesRegular from './use-stripes-regular';
import StripesContainer from '../components/stripes-container';
import { Props, PageType } from '../_utils/types';
import StripesSkeleton from '../components/stripes-skeleton';
import StripesUIElement from '../components/stripes-ui-element';
import { UIElementExtended } from '../_stores/store-stripes-base';
import { NetworkErrorDialog } from '@smart-tv/components/error-dialog/network-error-dialog';
import useNetwork from '@smart-tv/hooks/use-network/use-network';

const StripesRegular = observer(({ pageType }: Props) => {
  const {
    ref,
    focusKey,
    uiElements,
    activeUIElement,
    openCollections,
    handleonShowAllEnterPress,
    onUIElementFocus,
    onHeroUIElementFocus,
    universeTitle,
    error,
    retryOpenCollection,
    shouldFocusFirstElement
  } = useStripesRegular({ pageType });
  const { isOffline, isNetworkDialogSuppressed, setIsNetworkDialogSuppressed } = useNetwork();

  const [readyForFocus, setReadyForFocus] = useState(false);

  if (!uiElements?.length && !error) return <StripesSkeleton />;

  return (
    <>
      <StripesContainer focusKey={focusKey} scrollRef={ref}>
        {uiElements && uiElements.length > 0 && (
          <StripesUIElement
            universeTitle={universeTitle}
            uIElementIndex={0}
            key={uiElements[0].id}
            uiElement={uiElements[0]}
            onFocus={onHeroUIElementFocus}
            handleShowAllEnterPress={() => handleonShowAllEnterPress(uiElements[0])}
            contentSource={uiElements[0].contentSource}
            providerContentProps={uiElements[0].providerContentProps}
            style={uiElements[0].style}
            focusFirstElement={shouldFocusFirstElement}
            displayHero={true}
            fetchAll={activeUIElement === uiElements[0]}
            readyForFocus={readyForFocus}
          />
        )}
        {uiElements?.slice(1).map((uiElement: UIElementExtended, index: number) => {
          const indexIncrementet = index + 1;
          return (
            uiElement.hasItems && (
              <StripesUIElement
                universeTitle={universeTitle}
                uIElementIndex={indexIncrementet}
                key={uiElement.id}
                uiElement={uiElement}
                onFocus={onUIElementFocus}
                handleShowAllEnterPress={() => handleonShowAllEnterPress(uiElement)}
                contentSource={uiElement.contentSource}
                providerContentProps={uiElement.providerContentProps}
                style={uiElement.style}
                focusFirstElement={false}
                displayHero={false}
                fetchAll={activeUIElement === uiElement}
                //setReadyForFocus is only defined for a single item in a single stripe, so we only call it for the last stripe since we know that focus can no longer be lost after last stripe is rendered.
                setReadyForFocus={
                  indexIncrementet === uiElements.length - 1 ? setReadyForFocus : undefined
                }
              />
            )
          );
        })}
      </StripesContainer>

      {activeUIElement && openCollections && <Collections uiElement={activeUIElement} />}
      {isOffline &&
        (pageType === PageType.Universe ||
          (pageType === PageType.FrontPage && !isNetworkDialogSuppressed)) && (
          <NetworkErrorDialog
            onTryAgain={retryOpenCollection}
            setNetworkStatusOnRetry={true}
            onClose={() => {
              if (pageType === PageType.FrontPage) setIsNetworkDialogSuppressed(true);
            }}
          />
        )}
    </>
  );
});

export default StripesRegular;
